import moment from "moment";
import { useEffect, useState } from "react";
import {
  FiArrowDownRight,
  FiArrowUpRight,
  FiBox,
  FiCalendar,
  FiDollarSign,
  FiShoppingBag,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";
import { toast } from "react-toastify";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import SummaryApi from "../common";
import displayNPRCurrency from "../helpers/displayCurrency";
import { request } from "../helpers/request";

const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("30days");

  const COLORS = [
    "#FF4D4D",
    "#3B82F6",
    "#10B981",
    "#F59E0B",
    "#8B5CF6",
    "#EC4899",
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchAnalytics();
  }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await request({
        url: `${SummaryApi.getDetailedAnalytics.url}?range=${range}`,
        method: SummaryApi.getDetailedAnalytics.method,
      });
      const responseData = await response.json();

      if (responseData.success) {
        setData(responseData.data);
      } else {
        toast.error(responseData.message || "Failed to fetch analytics");
      }
    } catch (error) {
      toast.error("An error occurred while fetching analytics");
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, trend, color }) => (
    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-4">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="text-white text-xl" />
        </div>
        {trend && (
          <div
            className={`flex items-center text-xs font-bold ${trend > 0 ? "text-green-500" : "text-red-500"}`}
          >
            {trend > 0 ? <FiArrowUpRight /> : <FiArrowDownRight />}
            <span className="ml-1">{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      <div>
        <p className="text-gray-500 text-sm font-medium uppercase tracking-wider">
          {title}
        </p>
        <h3 className="text-2xl font-black text-gray-800 mt-1">{value}</h3>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!data) return null;

  const { stats, charts, topProducts } = data;

  return (
    <div className="p-4 md:p-8 space-y-8 animate-fadeIn">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-gray-800 tracking-tight">
            Analytics Dashboard
          </h1>
          <p className="text-gray-500">
            Track your business performance and growth.
          </p>
        </div>
        <div className="flex bg-gray-100 p-1 rounded-xl">
          {["7days", "30days", "90days", "year"].map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                range === r
                  ? "bg-white text-red-600 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {r === "30days"
                ? "Last Month"
                : r === "7days"
                  ? "Last Week"
                  : r === "90days"
                    ? "Last Quarter"
                    : "Last Year"}
            </button>
          ))}
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Revenue"
          value={displayNPRCurrency(stats.totalRevenue)}
          icon={FiDollarSign}
          color="bg-red-500"
        />
        <StatCard
          title="Total Orders"
          value={stats.totalOrders}
          icon={FiShoppingBag}
          color="bg-blue-500"
        />
        <StatCard
          title="Avg Order Value"
          value={displayNPRCurrency(stats.averageOrderValue)}
          icon={FiTrendingUp}
          color="bg-green-500"
        />
        <StatCard
          title="Total Customers"
          value={stats.totalUsers}
          icon={FiUsers}
          color="bg-purple-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Revenue Trend Chart */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-black text-gray-800 mb-6 flex items-center gap-2">
            <FiCalendar className="text-red-500" /> Revenue Trend
          </h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={charts.salesOverTime}>
                <defs>
                  <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF4D4D" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#FF4D4D" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#F3F4F6"
                />
                <XAxis
                  dataKey="_id"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: "#9CA3AF" }}
                  tickFormatter={(val) => moment(val).format("DD MMM")}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: "#9CA3AF" }}
                  tickFormatter={(val) => `₹${val}`}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: "12px",
                    border: "none",
                    boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                  }}
                  formatter={(value) => [displayNPRCurrency(value), "Revenue"]}
                  labelFormatter={(label) =>
                    moment(label).format("DD MMMM YYYY")
                  }
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#FF4D4D"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorRev)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Distribution Chart */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-black text-gray-800 mb-6 flex items-center gap-2">
            <FiBox className="text-blue-500" /> Revenue by Category
          </h3>
          <div className="h-[300px] w-full flex flex-col md:flex-row items-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={charts.categoryRevenue}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="revenue"
                  nameKey="_id"
                >
                  {charts.categoryRevenue.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    borderRadius: "12px",
                    border: "none",
                    boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                  }}
                  formatter={(value) => displayNPRCurrency(value)}
                />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Top Selling Products */}
        <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-black text-gray-800 mb-6">
            Top Selling Products
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b border-gray-50 uppercase text-[10px] tracking-widest text-gray-400">
                  <th className="pb-4 font-black">Product</th>
                  <th className="pb-4 font-black">Quantity Sold</th>
                  <th className="pb-4 font-black">Revenue</th>
                  <th className="pb-4 font-black text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {topProducts.map((product) => (
                  <tr
                    key={product._id}
                    className="group hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg p-1">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-contain mix-blend-multiply"
                          />
                        </div>
                        <span className="text-sm font-bold text-gray-800 line-clamp-1">
                          {product.name}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 font-bold text-gray-700 text-sm">
                      {product.totalSold} Units
                    </td>
                    <td className="py-4 font-black text-primary text-sm">
                      {displayNPRCurrency(product.revenue)}
                    </td>
                    <td className="py-4 text-right">
                      <button className="text-[10px] font-black uppercase text-accent hover:underline">
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* User Growth Chart (Bar) */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-black text-gray-800 mb-6 flex items-center gap-2">
            <FiUsers className="text-purple-500" /> New Signups
          </h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.userGrowth}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#F3F4F6"
                />
                <XAxis
                  dataKey="_id"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: "#9CA3AF" }}
                  tickFormatter={(val) => moment(val).format("DD")}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: "#9CA3AF" }}
                />
                <Tooltip
                  cursor={{ fill: "#F9FAFB" }}
                  contentStyle={{
                    borderRadius: "12px",
                    border: "none",
                    boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                  }}
                />
                <Bar
                  dataKey="count"
                  fill="#8B5CF6"
                  radius={[4, 4, 0, 0]}
                  barSize={10}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-6 pt-6 border-t border-gray-50 flex justify-between items-center">
            <div>
              <p className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                Total Customers
              </p>
              <h4 className="text-xl font-black text-gray-800 leading-tight">
                {stats.totalUsers}
              </h4>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                Growth Rate
              </p>
              <span className="text-sm font-black text-green-500">+12.5%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
